"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { FileText, Lock, LockOpen } from "lucide-react";
import { StatusChip, PriorityChip } from "@/components/actions/badges";
import { useUserStore, useHydrated } from "@/store/userStore";
import { useMapStore } from "@/store/mapStore";
import { useActionStore } from "@/store/actionStore";

function LoginForm() {
  const { login, loading, error, clearError } = useUserStore();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      let dest = localStorage.getItem("redirectAfterLogin") || "/";
      localStorage.removeItem("redirectAfterLogin");
      if (dest === "/doc/demo") dest = "/";
      await login(email, password);
      router.replace(dest);
    } catch {
      // error displayed via store
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f8",
      }}
    >
      <Card sx={{ width: 380, borderRadius: 3, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "primary.main", mb: 0.5, textAlign: "center" }}
          >
            Netwise
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            Sign in to continue
          </Typography>

          {error && (
            <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form">
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              slotProps={{ htmlInput: { "data-testid": "login-email" } }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              size="small"
              fullWidth
              sx={{ mb: 3 }}
              slotProps={{ htmlInput: { "data-testid": "login-password" } }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              data-testid="login-submit"
              sx={{
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Log in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

function Dashboard() {
  const router = useRouter();
  const { maps, loading: mapsLoading, fetchMaps } = useMapStore();
  const { items, loading: actionsLoading, fetchMine } = useActionStore();

  useEffect(() => {
    fetchMaps();
    fetchMine();
  }, [fetchMaps, fetchMine]);

  const recentMaps = maps;
  const recentActions = [...items]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 4 }} data-testid="home-page">
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 4 }}>
        Home
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        {/* Recent Maps */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Maps
            </Typography>
            <Link href="/maps" style={{ color: "var(--mui-palette-primary-main)", fontSize: 14, fontWeight: 600 }}>
              View All
            </Link>
          </Box>

          {mapsLoading && maps.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!mapsLoading && maps.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No maps yet.
            </Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {recentMaps.map((m) => (
              <Card key={m.uuid} sx={{ borderRadius: 2 }}>
                <CardContent
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" }, py: 1.5, "&:last-child": { pb: 1.5 } }}
                  onClick={() => router.push(`/doc/${m.uuid}`)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FileText size={18} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                      {m.rootName || "Untitled"}
                    </Typography>
                    {m.public ? (
                      <LockOpen size={14} color="#22c55e" />
                    ) : (
                      <Lock size={14} color="#6b7280" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Recent Action Items */}
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Action Items
            </Typography>
            <Link href="/actions" style={{ color: "var(--mui-palette-primary-main)", fontSize: 14, fontWeight: 600 }}>
              View All
            </Link>
          </Box>

          {actionsLoading && items.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!actionsLoading && items.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No action items yet.
            </Typography>
          )}

          {recentActions.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Assignee</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActions.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => router.push(`/doc/${item.mapId}`)}
                    >
                      <TableCell>{item.title}</TableCell>
                      <TableCell>
                        <StatusChip status={item.status} />
                      </TableCell>
                      <TableCell>
                        <PriorityChip priority={item.priority} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.assigneeEmail ?? "\u2014"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function RootPage() {
  const user = useUserStore((s) => s.user);
  const hydrated = useHydrated();

  if (!hydrated) return null;

  if (user) return <Dashboard />;

  return <LoginForm />;
}
